import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAppContext } from '../../context/AppContext';
import { useSubscription } from '../../hooks/useSubscription';
import DeckCard from './DeckCard';

export default function DecksPage() {
  const { decks, addDeck } = useAppContext();
  const { canAddDeck, limits, usedDecks } = useSubscription();
  const [newName, setNewName] = useState('');
  const [showForm, setShowForm] = useState(false);

  const handleCreate = () => {
    if (!newName.trim()) return;
    addDeck(newName.trim());
    setNewName('');
    setShowForm(false);
  };

  return (
    <div className="p-4 pt-14 md:p-8 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100">Decks</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            {usedDecks}/{limits.maxDecks === Infinity ? '∞' : limits.maxDecks} decks this month
          </p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          disabled={!canAddDeck && !showForm}
          className="px-4 py-2.5 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors disabled:bg-slate-300 dark:disabled:bg-slate-600 disabled:cursor-not-allowed"
        >
          {showForm ? 'Cancel' : '+ New Deck'}
        </button>
      </div>

      {!canAddDeck && (
        <div className="bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 text-amber-700 dark:text-amber-400 px-4 py-3 rounded-lg mb-6 text-sm">
          You've reached the limit of {limits.maxDecks} decks per month on your plan.{' '}
          <Link to="/pricing" className="font-medium underline">Upgrade</Link> for more.
        </div>
      )}

      {showForm && (
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6 mb-8">
          <h2 className="text-lg font-semibold text-slate-700 dark:text-slate-200 mb-4">Create a New Deck</h2>
          <div className="flex gap-3">
            <input
              type="text"
              placeholder="e.g. IT/Technology, Business Terms, JLPT N3..."
              value={newName}
              onChange={e => setNewName(e.target.value)}
              autoFocus
              onKeyDown={e => e.key === 'Enter' && handleCreate()}
              className="flex-1 px-4 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100"
            />
            <button
              onClick={handleCreate}
              disabled={!newName.trim()}
              className="px-6 py-2.5 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors disabled:bg-slate-300 dark:disabled:bg-slate-600 disabled:cursor-not-allowed"
            >
              Create
            </button>
          </div>
        </div>
      )}

      {decks.length === 0 ? (
        <div className="text-center py-20">
          <div className="text-4xl mb-4">🗂</div>
          <h3 className="text-lg font-medium text-slate-600 dark:text-slate-300 mb-2">No decks yet</h3>
          <p className="text-sm text-slate-400">Create a deck to start organizing your vocabulary</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {decks.map(deck => (
            <DeckCard key={deck.id} deck={deck} />
          ))}
        </div>
      )}
    </div>
  );
}
